import { ServerResponseError } from "./custom-errors";

/**
 * Performs an asynchronous fetch request with the given payload and options.
 * @param {object} payload - The payload to be sent in the request body.
 * @param {string} url - The URL to which the request is sent.
 * @param {object} [options] - Optional configurations for the fetch request.
 * @param {object} [options.headers] - Headers to be included in the request. (default="Content-Type": "application/json")
 * @param {string} [options.method] - HTTP method to be used for the request. (default="POST")
 * @param {AbortSignal} [options.signal] - An AbortSignal to abort the request.
 * @returns {Promise<object>} - A promise that resolves to the response data.
 * @throws {Error} - Throws an error if the fetch request fails.
 */
const doFetch = async (payload, url, options) => {
	if (!url || !payload) {
		console.error(`doFetch: Missing required ${url} or ${payload}`);
		return;
	}

	const headers = {
		"Content-Type": "application/json",
		...(options?.headers || {}),
	};
	const method = options?.method || "POST";
	const requestInit = {
		...options,
		body: JSON.stringify(payload),
		method,
		headers,
	};

	let response;
	try {
		const fetchResponse = await fetch(url, requestInit);

		if (fetchResponse && fetchResponse.ok) {
			response = await fetchResponse.json();
		} else {
			throw new Error(`${fetchResponse.status} - ${fetchResponse.statusText}`);
		}
	} catch (error) {
		throw new ServerResponseError("doFetch error from server", {
			cause: error,
			statusCode: error.response?.status,
			response: error.response,
		});
	}
	return response;
};

export { doFetch };
